import { filter, isEmpty, map } from "lodash";
import { AiccPromptConfig, AiccPromptVariable } from "types/model/aiccPrompt";
import {
  SAVE_BLOCKED_BY_EMPTY,
  SAVE_BLOCKED_BY_MALFORMED,
  SAVE_BLOCKED_BY_MISSING,
  SAVE_BLOCKED_BY_UNKNOWN,
  SAVE_BLOCKED_BY_UNSUPPORTED,
} from "../configs";
import { UnsupportedMarkdown, markdownToPromptBlocks, normalisePromptMarkdown } from "../../PromptEditor/markdown";
import { malformedVariablesIn, missingRequiredVariables, variableNamesInText } from "../../PromptEditor/variables";

export interface AiccPromptVariableHealth {
  name: string;
  isRequired: boolean;
  isPresent: boolean;
}

export interface AiccPromptDraftHealth {
  variables: AiccPromptVariableHealth[];
  missingRequired: string[];
  unknownVariables: string[];
  malformedVariables: string[];
  unsupported: UnsupportedMarkdown[];
  isEmpty: boolean;
  isDirty: boolean;
  canSave: boolean;
  blockedReason?: string;
}

export function promptDraftHealth(
  draft: AiccPromptConfig,
  saved: AiccPromptConfig,
  variables: AiccPromptVariable[]
): AiccPromptDraftHealth {
  const used = variableNamesInText(draft.promptText);
  const requiredNames = map(filter(variables, "isRequired"), "name");
  const missingRequired = missingRequiredVariables(draft.promptText, requiredNames);
  const isEmpty = !draft.promptText.trim();
  const knownNames = map(variables, "name");
  // 🔴 Markdown the editor cannot show would be deleted by a save of what is on screen, so it blocks Save.
  const { unsupported } = markdownToPromptBlocks(draft.promptText);
  const unknownVariables = filter(used, (name) => !knownNames.includes(name));
  const malformedVariables = malformedVariablesIn(draft.promptText);
  const isDirty =
    draft.modelId !== saved.modelId ||
    draft.temperature !== saved.temperature ||
    draft.reasoningEffort !== saved.reasoningEffort ||
    normalisePromptMarkdown(draft.promptText) !== normalisePromptMarkdown(saved.promptText);

  // 🔴 Dirty against the saved copy, not the default as AICC2-60 says: that would refuse editing a prompt back to default.
  const blockedReason = unsupported.length
    ? SAVE_BLOCKED_BY_UNSUPPORTED
    : malformedVariables.length
    ? SAVE_BLOCKED_BY_MALFORMED
    : missingRequired.length
    ? SAVE_BLOCKED_BY_MISSING
    : unknownVariables.length
    ? SAVE_BLOCKED_BY_UNKNOWN
    : isEmpty
    ? SAVE_BLOCKED_BY_EMPTY
    : undefined;

  return {
    variables: map(variables, ({ name, isRequired }) => ({
      name,
      isRequired,
      isPresent: used.includes(name),
    })),
    missingRequired,
    unknownVariables,
    malformedVariables,
    unsupported,
    isEmpty,
    isDirty,
    canSave: !blockedReason && isDirty,
    blockedReason,
  };
}

export function hasVariableHealth(health: AiccPromptDraftHealth): boolean {
  return !isEmpty(health.variables);
}
